import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Inline } from "../Inline";

const CAT_OPTS = [
  ["fp", "false alarms are costly"],
  ["fn", "missing a real case is dangerous"],
  ["bal", "need a single balanced score"],
  ["thr", "comparing models across thresholds"],
];
const NUM_OPTS = [
  ["big", "big misses hurt disproportionately"],
  ["out", "the data has outliers"],
  ["exp", "stakeholders want \"% of variance explained\""],
];

const M = {
  fp: "**Precision:** \"when I Point, am I right?\" Minimizes false alarms (spam filters, costly investigations). Linear Learner knob: `target_precision`↑.",
  fn: "**Recall:** \"did I Round them all up?\" Minimizes misses (cancer screening, fraud, intrusion detection).",
  bal: "**F1:** the harmonic balance of precision and recall; the default for imbalanced data. (Accuracy is only safe on BALANCED classes.)",
  thr: "**ROC-AUC:** plots performance at every classification threshold. The word \"threshold\" in a stem points here.",
  big: "**RMSE:** squares the errors, so large misses are punished extra. (Forecasting questions: RMSE + wQL.)",
  out: "**MAE:** plain average error, robust to outliers.",
  exp: "**R²:** the share of variance the model explains.",
};

function Choice({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`font-mono-ui text-[12px] px-3 py-2 border transition-colors duration-200 ${
        active ? "bg-[#1C2430] text-[#FBFBF9] border-[#1C2430]" : "bg-white border-[var(--border-heavy)] hover:bg-black/[0.03]"
      }`}
    >
      {children}
    </button>
  );
}

export function MetricPicker() {
  const [kind, setKind] = useState(null);
  const [pain, setPain] = useState(null);

  const pick = (k) => {
    setKind(k);
    setPain(null);
  };

  const opts = kind === "cat" ? CAT_OPTS : NUM_OPTS;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="my-12 border-2 border-[#2F6F4F] bg-white/70 backdrop-blur-sm p-7 md:p-9"
      data-testid="widget-metric-picker"
    >
      <div className="font-mono-ui text-[10px] font-bold uppercase tracking-[0.22em] text-[#2F6F4F] mb-6">
        Try it · Which metric? Answer two questions
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-[13.5px] mr-1">This model predicts a…</span>
        <Choice active={kind === "cat"} onClick={() => pick("cat")} testid="metric-kind-cat">category (yes/no, class)</Choice>
        <Choice active={kind === "num"} onClick={() => pick("num")} testid="metric-kind-num">number (price, demand)</Choice>
      </div>
      <AnimatePresence>
        {kind && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="text-[13.5px] mr-1">What hurts most?</span>
              {opts.map(([v, label]) => (
                <Choice key={v} active={pain === v} onClick={() => setPain(v)} testid={`metric-pain-${v}`}>
                  {label}
                </Choice>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="bg-black/[0.03] px-4 py-3 text-[13.5px] leading-relaxed text-[#1C2430]/85 min-h-[48px]" data-testid="metric-output">
        {pain ? <Inline text={M[pain]} /> : kind ? "Now pick what hurts most." : "Pick what the model predicts to begin."}
      </div>
    </motion.div>
  );
}
